import React, { useEffect, useState, useRef } from 'react';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { WarningOutlined, InfoCircleFilled } from '@ant-design/icons';
import Modal, { useModal } from 'components/Modal';
import Form, { useForm } from 'components/Form';
import Steps, { Step } from 'components/Steps';
import toast from 'helper/toast';
import OrderTypeStep from './steps/OrderType';
import OrderDetailsStep from './steps/OrderDetails';
import PaymentsStep from './steps/Payments';
import ConfirmStep from './steps/Confirm';
import { useActions, useNewOrderModalData } from '../../selectorData';
import {
  getModalProps,
  getInitialValues,
  prepareFormData,
  prepareFormDataFromValues,
  buildCartOrderSnapshot,
  restoreCartOrderSnapshot,
  orderTypeValidateObj,
  prefillFormEditMode,
  getCardBR,
} from './utils';
import { steps, ORDER_FLOW_TYPES } from './constants';
import CartSidebar from './components/CartSidebar';
import classes from './NewOrderModal.module.scss';
import { scrollToErrorField, validateForm } from '../../../../../utils/form';
import { actionCb } from 'utils/action';
import { getErrorMessageFromObj } from 'utils/object';

const NewOrderModal = ({
  clientId,
  isOpen,
  setActiveTab,
  handleClose,
  orderDetailData,
  isEditOrder,
  orderDetailFieldValues,
  onSubmitOrderCb,
  onSaveDraft,
  onSubmitOrder,
  onSaveDraftCb,
  onSubmitOrderICb,
  // prefillData is fields array of ant-form which can use form.setFields
  prefillData,
  fromReminder,
  permission,
  initialCartOrders = [],
  initialCartId = null,
  initialSelectedPackage = null,
  initialPackageOrderIndex = 0,
}) => {
  const [form] = useForm();
  const {
    getAccounts,
    getFuelCards,
    saveOrderAsDraft,
    submitOrder,
    getOrderList,
    getBankName,
    createFuelCard,
    getOrderTypeDetail,
    getOrderDetailFields,
    addOrderToCart,
    removeCartItem,
    cartCheckout,
    getPackages,
    getClientOrderFieldValues,
  } = useActions();
  const { detailData, orderTypes, orderTypeDetail } = useNewOrderModalData();
  const [addItems, setAddItems] = useState({});
  const [cardError, setCardError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [orderTypeId, setOrderTypeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formDetailsFieldPopulated, setFormDetailsFieldPopulated] = useState(
    false
  );
  const [cartOrders, setCartOrders] = useState([]);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [cartId, setCartId] = useState(null);
  const [orderFlowType, setOrderFlowType] = useState(ORDER_FLOW_TYPES.SIMPLE);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageOrderIndex, setPackageOrderIndex] = useState(0);
  const [modal, contextHolder] = useModal();
  const orderTypeStep = useRef();
  const orderDetailsStep = useRef();
  const paymentStep = useRef();
  const confirmStep = useRef();
  const [cartPricingSnapshot, setCartPricingSnapshot] = useState({
    items: [],
    grandTotal: 0,
  });
  const handleCardError = (err, onSubmit, onClose) => {
    const tMessage = err?.details || getErrorMessageFromObj(err)[0] || '';
    setCardError(tMessage);

    const messageArr = tMessage.split(': ');

    let modalInstance;

    modalInstance = modal.confirm({
      title: messageArr[0] || '',
      content: <div>{messageArr[1] || ''}</div>,
      width: 295,
      okText: 'Update',
      cancelText: 'Close',
      centered: true,
      icon: <InfoCircleFilled />,
      className: classes.cardDeclineModal,

      onOk: () => {
        if (onSubmit) onSubmit();
        if (modalInstance) modalInstance.destroy();
      },

      onCancel: () => {
        if (onClose) onClose();
        if (modalInstance) modalInstance.destroy();
      },
    });
  };

  const navigateToPreviousStep = step => {
    if (step === undefined) {
      step = currentStep - 1;
    }
    setCurrentStep(step);
  };
  const saveDraft = () => {
    const values = form.getFieldsValue();
    const fieldErrors = validateForm(orderTypeValidateObj, values);
    if (fieldErrors.length > 0) {
      form.setFields(fieldErrors);
      scrollToErrorField();
    } else {
      const formData = prepareFormData(
        form,
        isEditOrder ? orderDetailData : {},
        false,
        addItems
      );
      if (onSaveDraft) {
        onSaveDraft(formData, () => {});
      } else {
        setLoading(true);
        saveOrderAsDraft(detailData.id, formData, (response, error) => {
          setOrderTypeId(null);
          setStepDescription(0, '');
          form.resetFields();
          setLoading(false);
          if (!response) {
            toast.error(error.error);
            handleClose();
            form.resetFields();
            return;
          }
          // If a cart already exists, silently add this draft to it
          if (cartId) {
            addOrderToCart(
              detailData.id,
              { order_id: response.data.id, cart_id: cartId },
              () => {}
            );
          }
          if (onSaveDraftCb) onSaveDraftCb();
          getOrderList(detailData.id, { highlight: response.data.id }, () => {
            toast.success('Draft saved successfully.');
            handleClose();
            setActiveTab(2);
            getAccounts(clientId);
            getFuelCards(clientId);
          });
        });
      }
    }
  };
  const resetCartState = () => {
    setCartOrders([]);
    setEditingCartIndex(null);
    setCartId(null);
    setSelectedPackage(null);
    setPackageOrderIndex(0);
  };

  // Sequentially submits an array of FormData objects, one per order.
  const submitAllOrdersSequentially = (formDataArray, totalCount) => {
    const doSubmit = (remaining, lastId) => {
      if (remaining.length === 0) {
        setOrderTypeId(null);
        setStepDescription(0, '');
        form.resetFields();
        setLoading(false);
        resetCartState();
        if (onSubmitOrderICb) onSubmitOrderICb();
        getOrderList(detailData.id, { highlight: lastId }, () => {
          toast.success(
            totalCount > 1
              ? `${totalCount} orders submitted successfully.`
              : 'Order submitted successfully.'
          );
          handleClose();
          setActiveTab(2);
          getAccounts(clientId, {}, accountRes => {
            getFuelCards(clientId, {}, cardRes => {
              if (onSubmitOrderCb) {
                onSubmitOrderCb({
                  accounts: accountRes?.data || [],
                  cards: cardRes?.data || [],
                });
              }
            });
          });
        });
        return;
      }
      const [current, ...rest] = remaining;
      submitOrder(detailData.id, current, (response, error) => {
        if (!response) {
          form.resetFields();
          setLoading(false);
          resetCartState();
          toast.error(error.message || error.title);
          handleClose();
          return;
        }
        doSubmit(rest, response.data?.id);
      });
    };
    doSubmit(formDataArray, null);
  };

  const submitForm = () => {
    if (onSubmitOrder) {
      const formData = prepareFormData(
        form,
        isEditOrder ? orderDetailData : {},
        fromReminder,
        addItems,
        true
      );
      onSubmitOrder(formData, () => {});
      return;
    }
    setLoading(true);
    const values = form.getFieldsValue();
    const isNewCard = !values.payment_method_id && !isEmpty(values.card);
    setCardError('');

    const doSubmit = paymentMethodId => {
      const latestValues = form.getFieldsValue();

      // ── Cart checkout flow: multiple orders saved to backend cart ──
      if (cartId) {
        const ordersPayload = cartOrders.map((snapshot, idx) => {
          const price =
            Number(
              latestValues[`cart_price_${idx}`] ?? snapshot.formValues.price
            ) || 0;
          const rushChecked = !!(
            latestValues[`cart_rush_order_${idx}`]?.length ??
            snapshot.formValues.rush_order?.length
          );
          return {
            order_id: snapshot.draftOrderId,
            is_rush_order: rushChecked,
            price,
            requested_by: snapshot.formValues.requested_by,
            notes: latestValues.notes || snapshot.formValues.notes || null,
          };
        });
        const checkoutPayload = {
          cart_id: cartId,
          orders: ordersPayload,
          payment_method_id: paymentMethodId || null,
          payment_method: latestValues.payment_method || null,
        };
        cartCheckout(detailData.id, checkoutPayload, (response, error) => {
          if (!response) {
            setLoading(false);
            toast.error(error?.message || 'Checkout failed.');
            return;
          }
          const totalCount = cartOrders.length;
          setOrderTypeId(null);
          setStepDescription(0, '');
          form.resetFields();
          setLoading(false);
          resetCartState();
          if (onSubmitOrderICb) onSubmitOrderICb();
          getOrderList(detailData.id, {}, () => {
            toast.success(
              totalCount > 1
                ? `${totalCount} orders submitted successfully.`
                : 'Order submitted successfully.'
            );
            handleClose();
            setActiveTab(2);
            getAccounts(clientId, {}, accountRes => {
              getFuelCards(clientId, {}, cardRes => {
                if (onSubmitOrderCb) {
                  onSubmitOrderCb({
                    accounts: accountRes?.data || [],
                    cards: cardRes?.data || [],
                  });
                }
              });
            });
          });
        });
        return;
      }

      // ── Single order flow: no backend cart (first order via Next button) ──
      const allFormDataArray = [];
      if (cartOrders.length > 0) {
        cartOrders.forEach((snapshot, idx) => {
          const updatedFormValues = {
            ...snapshot.formValues,
            price:
              latestValues[`cart_price_${idx}`] ?? snapshot.formValues.price,
            rush_order:
              latestValues[`cart_rush_order_${idx}`] ??
              snapshot.formValues.rush_order,
            notes: latestValues.notes ?? snapshot.formValues.notes,
          };
          const fd = prepareFormDataFromValues(
            updatedFormValues,
            isEditOrder ? orderDetailData : {},
            fromReminder,
            snapshot.addItems,
            true
          );
          fd.append(
            'payment_method_id',
            paymentMethodId !== undefined ? paymentMethodId : ''
          );
          // payment_method may be absent if the Payments FormItem was never registered
          // (conditionally rendered behind isPaymentNeeded). Guarantee it is always sent.
          fd.set(
            'payment_method',
            latestValues.payment_method ||
              form.getFieldValue('payment_method') ||
              'card'
          );
          allFormDataArray.push(fd);
        });
      } else {
        // Edit order / edge case — submit the live form directly
        const currentFd = prepareFormData(
          form,
          isEditOrder ? orderDetailData : {},
          fromReminder,
          addItems,
          true
        );
        currentFd.append(
          'payment_method_id',
          paymentMethodId !== undefined ? paymentMethodId : ''
        );
        // payment_method may be absent if the Payments FormItem was never registered
        // (conditionally rendered behind isPaymentNeeded). Guarantee it is always sent.
        currentFd.set(
          'payment_method',
          latestValues.payment_method ||
            form.getFieldValue('payment_method') ||
            'card'
        );
        allFormDataArray.push(currentFd);
      }
      submitAllOrdersSequentially(allFormDataArray, allFormDataArray.length);
    };

    if (isNewCard) {
      createFuelCard(
        detailData.id,
        { payment_cards: [getCardBR(values.card)] },
        actionCb(
          res => {
            const tPaymentId = get(res, 'data[0].id');
            doSubmit(tPaymentId);
          },
          err => {
            setLoading(false);
            handleCardError(
              err,
              () => {
                navigateToPreviousStep();
              },
              () => {}
            );
          },
          true
        )
      );
    } else {
      doSubmit(
        values.payment_method_id !== undefined ? values.payment_method_id : ''
      );
    }
  };

  // Saves the current order (step 0+1) into the cart and resets for a new entry.
  // Shared helper: save draft + add to cart, then run onSuccess(snapshot).
  const saveDraftAndAddToCart = (onSuccess, navigateStep) => {
    const values = form.getFieldsValue();
    if (!values.price && orderTypeDetail && orderTypeDetail.price) {
      form.setFieldsValue({ price: orderTypeDetail.price });
    }
    // Guarantee order_type_id is present in the form before building FormData
    if (orderTypeId && !form.getFieldValue('order_type_id')) {
      form.setFieldsValue({ order_type_id: orderTypeId });
    }
    const formData = prepareFormData(
      form,
      // When editingCartIndex is null and the cart already has items, the user is
      // adding a brand-new order (not re-editing the primary draft). Pass empty
      // orderDetailData so prepareFormData does NOT append the original order's id
      // — otherwise save-draft would update that order instead of creating a new one.
      isEditOrder && !(editingCartIndex === null && cartOrders.length > 0)
        ? orderDetailData
        : {},
      fromReminder,
      addItems,
      true // ignoreCard — payment_method_id must not be sent to the save-draft API
    );
    // In package flow order_type_id is not a registered form field (package_id is rendered
    // instead), so form.getFieldsValue() silently omits it. Explicitly set it here so the
    // draft API always receives a value. FormData.set() replaces any existing entry with
    // the same key, so this is safe for simple flow too.
    if (orderTypeId) {
      formData.set('order_type_id', orderTypeId);
    }
    // When navigating Back in pkg flow and clicking Next again, the cart snapshot
    // already holds a draftOrderId for this position. Send it as `id` so the
    // save-draft API updates the existing draft instead of creating a new one.
    if (
      editingCartIndex !== null &&
      cartOrders[editingCartIndex]?.draftOrderId
    ) {
      formData.set('id', cartOrders[editingCartIndex].draftOrderId);
    }
    setLoading(true);
    saveOrderAsDraft(detailData.id, formData, (draftResponse, draftError) => {
      if (!draftResponse) {
        setLoading(false);
        toast.error(draftError?.error || 'Failed to save draft.');
        return;
      }
      const draftOrderId = draftResponse.data.id;
      // Always call addOrderToCart after save-draft in cart flows.
      // If the order already has a cart item, keep it only as a fallback so
      // the local snapshot retains the cart item id even if the API response
      // does not include the matching item in its payload.
      const cartEditSnapshot =
        editingCartIndex !== null ? cartOrders[editingCartIndex] : null;
      const existingCartItem =
        cartEditSnapshot && cartEditSnapshot.cartItemId
          ? { id: cartEditSnapshot.cartItemId, cart_id: cartId }
          : isEditOrder
          ? orderDetailData.cart_item
          : null;
      const doAddToCart = (resolvedCartItemId, resolvedCartId) => {
        setLoading(false);
        if (resolvedCartId) setCartId(resolvedCartId);
        const foundOrderType = find(orderTypes, {
          value: values.order_type_id || orderTypeId,
        });
        const snapshot = buildCartOrderSnapshot(
          form,
          orderTypeDetail,
          foundOrderType?.label || '',
          addItems,
          draftOrderId,
          resolvedCartItemId
        );
        // The price InputItem is mounted in the Payments step (step 2), not in
        // OrderDetails (step 1). When saveDraftAndAddToCart runs at step 1 the
        // field is unmounted so form.getFieldsValue() returns price: 0.
        // Override with the order type's fixed price so the sidebar and Payments
        // overview show the correct amount.
        if (
          !snapshot.formValues.price &&
          orderTypeDetail &&
          orderTypeDetail.price
        ) {
          snapshot.formValues.price = orderTypeDetail.price;
        }
        if (onSuccess) onSuccess(snapshot);
        // Reset the active order form keeping rush_order initialized.
        // Explicitly clear order_type_id because form.resetFields() restores
        // Ant Design initialValues (which include order_type_id in edit mode),
        // causing the Payments step to render a duplicate "current order" row.
        setOrderTypeId(null);
        setStepDescription(0, '');
        setAddItems({});
        form.resetFields();
        form.setFieldsValue({ order_type_id: null, rush_order: [] });
        form.validateFields();
        setFormDetailsFieldPopulated(false);
        if (typeof navigateStep === 'function') {
          navigateStep();
        } else if (navigateStep !== undefined) {
          navigateToNextStep(navigateStep);
        } else {
          setCurrentStep(0);
        }
      };
      const cartPayload = { order_id: draftOrderId };
      if (cartId) cartPayload.cart_id = cartId;
      if (selectedPackage && selectedPackage.code) {
        cartPayload.package_code = selectedPackage.code;
      }
      addOrderToCart(detailData.id, cartPayload, (cartResponse, cartError) => {
        if (!cartResponse) {
          setLoading(false);
          toast.error(cartError?.error || 'Failed to add order to cart.');
          return;
        }
        const cartItem = (cartResponse.data.items || []).find(
          i => i.order_id === draftOrderId
        );
        const resolvedCartItemId = cartItem
          ? cartItem.id
          : existingCartItem?.id || null;
        const resolvedCartId =
          cartResponse.data.id || existingCartItem?.cart_id;
        doAddToCart(resolvedCartItemId, resolvedCartId);
      });
    });
  };

  const handleSaveAndAddMore = () => {
    if (orderDetailsStep.current && orderDetailsStep.current.validateStep) {
      orderDetailsStep.current.validateStep(isValidated => {
        if (!isValidated) return;
        saveDraftAndAddToCart(snapshot => {
          if (editingCartIndex !== null) {
            const updatedCart = [...cartOrders];
            updatedCart[editingCartIndex] = snapshot;
            setCartOrders(updatedCart);
            setEditingCartIndex(null);
          } else {
            setCartOrders(prev => [...prev, snapshot]);
          }
        });
      });
    }
  };

  const handleEditCartOrder = index => {
    const snapshot = cartOrders[index];
    const {
      orderTypeId: restoredId,
      addItems: restoredAddItems,
    } = restoreCartOrderSnapshot(form, snapshot);
    setOrderTypeId(restoredId);
    setAddItems(restoredAddItems);
    setStepDescription(0, snapshot.orderTypeName || '');
    setFormDetailsFieldPopulated(true);
    setEditingCartIndex(index);
    setPackageOrderIndex(index);
    // Re-fetch orderTypeDetail and field definitions for this order type
    // so OrderDetails renders the correct fields.
    getOrderTypeDetail(restoredId, () => {
      getOrderDetailFields(detailData.id, restoredId, () => {
        // If this order has a saved draft, fetch its field values so
        // prefillFormEditMode populates the form with saved data.
        if (snapshot.draftOrderId) {
          getClientOrderFieldValues(detailData.id, snapshot.draftOrderId);
        }
        setCurrentStep(1);
      });
    });
  };

  const handleDeleteCartOrder = index => {
    const snapshot = cartOrders[index];
    const doRemoveLocally = () => {
      const updatedCart = cartOrders.filter((_, i) => i !== index);
      setCartOrders(updatedCart);
      if (editingCartIndex === index) {
        setEditingCartIndex(null);
        setOrderTypeId(null);
        setAddItems({});
        form.resetFields();
        form.validateFields();
        setCurrentStep(0);
      } else if (editingCartIndex !== null && editingCartIndex > index) {
        setEditingCartIndex(editingCartIndex - 1);
      }
      // If the cart is now empty (last order deleted), always return to step 0
      // regardless of which step was active (e.g. Payments or Confirm).
      if (updatedCart.length === 0) {
        setEditingCartIndex(null);
        setOrderTypeId(null);
        setAddItems({});
        form.resetFields();
        form.setFieldsValue({ order_type_id: null, rush_order: [] });
        form.validateFields().catch(() => {});
        setFormDetailsFieldPopulated(false);
        setCartId(null);
        setCurrentStep(0);
      }
    };
    if (snapshot && snapshot.cartItemId) {
      removeCartItem(detailData.id, snapshot.cartItemId, (response, error) => {
        if (!response) {
          toast.error(error?.error || 'Failed to remove item from cart.');
          return;
        }
        doRemoveLocally();
      });
    } else {
      doRemoveLocally();
    }
  };
  const handleNextClick = () => {
    // When the user is on step 0 (order type selector), has items already in the
    // cart, and has NOT started picking a new order type → skip straight to
    // Payments (step 2) without requiring the step-0 fields to be filled.
    if (
      currentStep === 0 &&
      cartOrders.length > 0 &&
      orderFlowType !== ORDER_FLOW_TYPES.PACKAGE
    ) {
      const selectedOrderTypeId = form.getFieldValue('order_type_id');
      if (!selectedOrderTypeId) {
        navigateToNextStep(2);
        return;
      }
    }
    const modalSteps = [
      orderTypeStep,
      orderDetailsStep,
      paymentStep,
      confirmStep,
    ];
    if (
      modalSteps[currentStep].current &&
      modalSteps[currentStep].current.validateStep
    ) {
      modalSteps[currentStep].current.validateStep(
        (isValidated, isLastStep) => {
          if (!isValidated) {
            return;
          }
          // On step 1 (Order Details), when there are already cart items:
          // save draft + add to cart, then navigate to Payments.
          // When it's the very first order (empty cart), just proceed locally.
          if (currentStep === 1) {
            // ── Package flow: save draft + load next order type or go to Payments ──
            if (orderFlowType === ORDER_FLOW_TYPES.PACKAGE && selectedPackage) {
              const requestedBy = form.getFieldValue('requested_by');
              let updatedCartRef = null;
              saveDraftAndAddToCart(
                snapshot => {
                  // If user went Back to edit a previous pkg order, update it
                  // in place rather than pushing a duplicate entry.
                  let updatedCart;
                  if (editingCartIndex !== null) {
                    updatedCart = [...cartOrders];
                    updatedCart[editingCartIndex] = snapshot;
                    setCartOrders(updatedCart);
                    setEditingCartIndex(null);
                  } else {
                    updatedCart = [...cartOrders, snapshot];
                    setCartOrders(updatedCart);
                  }
                  updatedCartRef = updatedCart;
                },
                () => {
                  const latestCart = updatedCartRef || cartOrders;
                  const nextIndex = packageOrderIndex + 1;
                  if (nextIndex < selectedPackage.order_types.length) {
                    if (latestCart[nextIndex]) {
                      // Next order already created — restore it for editing
                      handleEditCartOrder(nextIndex);
                    } else {
                      const nextOrderType =
                        selectedPackage.order_types[nextIndex];
                      setPackageOrderIndex(nextIndex);
                      getOrderDetailFields(
                        detailData.id,
                        nextOrderType.id,
                        () => {
                          getOrderTypeDetail(nextOrderType.id, () => {
                            setOrderTypeId(nextOrderType.id);
                            setFormDetailsFieldPopulated(false);
                            setStepDescription(0, nextOrderType.name);
                            form.setFieldsValue({
                              order_type_id: nextOrderType.id,
                              requested_by: requestedBy,
                            });
                            // Stay on step 1
                          });
                        }
                      );
                    }
                  } else {
                    navigateToNextStep(2);
                  }
                }
              );
              return;
            }
            // Only send to backend for actual cart/package flows
            if (
              cartOrders.length > 0 &&
              (!!cartId || orderFlowType === ORDER_FLOW_TYPES.PACKAGE)
            ) {
              // Already have cart items — persist this order as draft + add to cart
              saveDraftAndAddToCart(snapshot => {
                if (editingCartIndex !== null) {
                  const updatedCart = [...cartOrders];
                  updatedCart[editingCartIndex] = snapshot;
                  setCartOrders(updatedCart);
                  setEditingCartIndex(null);
                } else {
                  setCartOrders(prev => [...prev, snapshot]);
                }
              }, 2);
              return;
            }
            // First (and only) order — no cart yet, add to local cart state only
            const values = form.getFieldsValue();
            if (!values.price && orderTypeDetail && orderTypeDetail.price) {
              form.setFieldsValue({ price: orderTypeDetail.price });
            }
            const foundOrderType = find(orderTypes, {
              value: values.order_type_id,
            });
            const snapshot = buildCartOrderSnapshot(
              form,
              orderTypeDetail,
              foundOrderType?.label || '',
              addItems,
              // In edit mode the order already exists — carry its id and cart item
              // id forward so the checkout payload sends the correct order_id.
              isEditOrder ? orderDetailData.id : undefined,
              isEditOrder ? orderDetailData.cart_item?.id : undefined
            );
            if (editingCartIndex !== null) {
              const updatedCart = [...cartOrders];
              updatedCart[editingCartIndex] = snapshot;
              setCartOrders(updatedCart);
              setEditingCartIndex(null);
            } else {
              setCartOrders(prev => [...prev, snapshot]);
            }
            setOrderTypeId(null);
            setStepDescription(0, '');
            setAddItems({});
            form.resetFields();
            form.setFieldsValue({ order_type_id: null, rush_order: [] });
            form.validateFields();
            setFormDetailsFieldPopulated(false);
            navigateToNextStep(2);
            return;
          }
          if (!isLastStep) {
            navigateToNextStep();
          } else {
            submitForm();
          }
        }
      );
    }
  };
  const closeModal = () => {
    modal.confirm({
      title: 'Leave Creating New Order?',
      content: (
        <div>
          You have started creating a new order. Are you sure you want to leave
          this page?
        </div>
      ),
      width: 423,
      centered: true,
      icon: <WarningOutlined />,
      okText: 'Yes, Leave',
      cancelText: 'No, Stay',
      onOk: () => {
        setOrderTypeId(null);
        setStepDescription(0, '');
        form.resetFields();
        resetCartState();
        setOrderFlowType(ORDER_FLOW_TYPES.SIMPLE);
        handleClose();
        return Promise.resolve();
      },
      onCancel: () => {
        return Promise.resolve();
      },
    });
  };
  useEffect(() => {
    if (isOpen && form) {
      setCardError('');
      setAddItems({});
      setCurrentStep(0);
      setStepDescription(0, '');
      form.resetFields();
      // form.resetFields() restores Ant Design's *cached* initialValues — which
      // may still hold a previous edit order's data. Explicitly re-seed with the
      // correct values for this open to guarantee a clean slate for new orders.
      form.setFieldsValue(getInitialValues(isEditOrder ? orderDetailData : {}));
      form.validateFields();
      setFormDetailsFieldPopulated(false);
      // In edit mode, initialize orderTypeId so the OrderType step knows
      // the original order type and can skip getOrderDetailFields on first
      // open while still calling it for new orders added via Save & Add More.
      if (isEditOrder && orderDetailData?.order_type_id) {
        setOrderTypeId(orderDetailData.order_type_id);
      } else {
        setOrderTypeId(null);
      }
      setCartOrders([]);
      setEditingCartIndex(null);
      setOrderFlowType(ORDER_FLOW_TYPES.SIMPLE);
      setSelectedPackage(null);
      setPackageOrderIndex(0);
      // If opening an edit for a cart-based draft, pre-populate sibling cart orders
      if (isEditOrder && initialCartOrders.length > 0) {
        setCartOrders(initialCartOrders);
      }
      if (isEditOrder && initialCartId) {
        setCartId(initialCartId);
      }
      // If opening a package edit, restore the package flow state so the sidebar
      // renders all order types and the correct one shows as active for editing.
      if (isEditOrder && initialSelectedPackage) {
        setSelectedPackage(initialSelectedPackage);
        setOrderFlowType(ORDER_FLOW_TYPES.PACKAGE);
        setPackageOrderIndex(initialPackageOrderIndex);
        setEditingCartIndex(initialPackageOrderIndex);
        // Pre-select the package in the dropdown
        form.setFieldsValue({ package_id: initialSelectedPackage.code });
      }
      if (!!prefillData && isArray(prefillData)) {
        form.setFields(prefillData);
      }
    }
    if (isOpen) {
      setLoading(false);
      getPackages();
      // Load payment methods immediately so the Payments step dropdown is
      // populated when the user arrives — especially critical in the edit flow
      // where accounts/cards are not loaded by any prior navigated step.
      getAccounts(clientId);
      getFuelCards(clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    if (isOpen && form && isEditOrder) {
      // TODO: calculate for addItems and prepopulate data
      prefillFormEditMode(orderDetailFieldValues, form, setAddItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetailFieldValues]);
  const navigateStep = step => {
    // When navigating to Order Details (step 1) with cart orders,
    // restore the cart order with all draft data and APIs.
    // For backend carts (cartId) or package flows: use handleEditCartOrder (full restoration + APIs)
    // For simple flows without cartId: just restore state without APIs
    if (step === 1 && cartOrders.length > 0 && currentStep !== 1) {
      if (!!cartId || orderFlowType === ORDER_FLOW_TYPES.PACKAGE) {
        // Restore the order that was being edited, or the last one in the cart
        const targetIndex =
          editingCartIndex !== null ? editingCartIndex : cartOrders.length - 1;
        handleEditCartOrder(targetIndex);
        return;
      }

      // Simple flow: restore state from snapshot without APIs
      if (orderFlowType === ORDER_FLOW_TYPES.SIMPLE && !cartId) {
        const targetIndex =
          editingCartIndex !== null ? editingCartIndex : cartOrders.length - 1;
        const snapshot = cartOrders[targetIndex];
        const {
          orderTypeId: restoredId,
          addItems: restoredAddItems,
        } = restoreCartOrderSnapshot(form, snapshot);
        setOrderTypeId(restoredId);
        setAddItems(restoredAddItems);
        setStepDescription(0, snapshot.orderTypeName || '');
        setFormDetailsFieldPopulated(true);
        setEditingCartIndex(targetIndex);
      }
    }

    setCurrentStep(step);
  };
  const navigateToNextStep = step => {
    if (step === undefined) {
      step = currentStep + 1;
    }
    setCurrentStep(step);
  };
  // In the package flow, Back should restore the previous package order instead
  // of blindly decrementing the wizard step. For simple/cart flows, back allows
  // full backward navigation through steps.
  const handleBackClick = () => {
    // Package flow: handle package order sequencing
    if (orderFlowType === ORDER_FLOW_TYPES.PACKAGE && currentStep === 1) {
      // If this is the first order (index 0), prevent going back to step 0
      if (packageOrderIndex === 0) {
        return; // Do nothing - button should be disabled
      }
      // If there are previous orders, go back to edit that order
      if (packageOrderIndex > 0) {
        const prevIndex = packageOrderIndex - 1;
        setPackageOrderIndex(prevIndex);
        handleEditCartOrder(prevIndex);
        return;
      }
    }

    // Cart flow: coming from Payments step (step 2) with backend cart
    // Restore the last order with all its data and APIs
    if (!!cartId && currentStep === 2 && cartOrders.length > 0) {
      const lastOrderIndex = cartOrders.length - 1;
      handleEditCartOrder(lastOrderIndex);
      return;
    }

    // Simple flow: coming from Payments step (step 2) without backend cart
    // Restore state from snapshot without APIs
    if (
      orderFlowType === ORDER_FLOW_TYPES.SIMPLE &&
      !cartId &&
      currentStep === 2 &&
      cartOrders.length > 0
    ) {
      const lastOrderIndex = cartOrders.length - 1;
      const snapshot = cartOrders[lastOrderIndex];
      const {
        orderTypeId: restoredId,
        addItems: restoredAddItems,
      } = restoreCartOrderSnapshot(form, snapshot);
      setOrderTypeId(restoredId);
      setAddItems(restoredAddItems);
      setStepDescription(0, snapshot.orderTypeName || '');
      setFormDetailsFieldPopulated(true);
      setEditingCartIndex(lastOrderIndex);
      navigateToPreviousStep();
      return;
    }

    // All other cases: normal backward navigation (just move step backward)
    navigateToPreviousStep();
  };
  const setStepDescription = (step, description) => {
    steps[step].description = description;
  };
  const modalProps = getModalProps({
    isOpen,
    form,
    handleNextClick,
    saveDraft,
    closeModal,
    loading,
    currentStep,
    handleBackClick,
    handleSaveAndAddMore,
    cartOrders,
    orderFlowType,
    selectedPackage,
    packageOrderIndex,
  });
  const stepProps = {
    form,
    setStepDescription,
    currentStep,
    orderTypeId,
    setOrderTypeId,
    getBankName,
    formDetailsFieldPopulated,
    setFormDetailsFieldPopulated,
    permission,
    addItems,
    setAddItems,
    orderFlowType,
    setOrderFlowType,
    cartOrders,
    editingCartIndex,
    selectedPackage,
    setSelectedPackage,
    packageOrderIndex,
    setPackageOrderIndex,
    isEditOrder,
  };
  return (
    <Modal {...modalProps}>
      <Form
        submitHidden
        initialValues={getInitialValues(isEditOrder ? orderDetailData : {})}
        form={form}
        className={classes.form}
      >
        <div className="ant-modal-body-inner">
          <div className={classes.cartLayout}>
            {(!!cartId || orderFlowType === ORDER_FLOW_TYPES.PACKAGE) && (
              <CartSidebar
                cartOrders={cartOrders}
                editingCartIndex={editingCartIndex}
                onEdit={handleEditCartOrder}
                onDelete={handleDeleteCartOrder}
                orderFlowType={orderFlowType}
                selectedPackage={selectedPackage}
                packageOrderIndex={packageOrderIndex}
                cartTotal={cartPricingSnapshot.grandTotal}
              />
            )}
            <div className={classes.formArea}>
              <Steps onChange={navigateStep} current={currentStep}>
                {steps.map((item, index) => (
                  <Step
                    className={classes.orderModalStep}
                    key={item.title}
                    title={item.title}
                    description={item.description}
                    disabled={
                      index >= currentStep ||
                      ((orderFlowType === ORDER_FLOW_TYPES.PACKAGE ||
                        (orderFlowType === ORDER_FLOW_TYPES.SIMPLE &&
                          !cartId)) &&
                        index === 0 &&
                        currentStep > 0)
                    }
                  />
                ))}
              </Steps>
              <OrderTypeStep
                style={{ display: currentStep === 0 ? 'block' : 'none' }}
                ref={orderTypeStep}
                {...stepProps}
                isEditOrder={isEditOrder}
              />
              <OrderDetailsStep
                style={{ display: currentStep === 1 ? 'block' : 'none' }}
                ref={orderDetailsStep}
                {...stepProps}
                isEditOrder={isEditOrder}
              />
              <PaymentsStep
                style={{ display: currentStep === 2 ? 'block' : 'none' }}
                ref={paymentStep}
                cardError={cardError}
                onPricingChange={setCartPricingSnapshot}
                {...stepProps}
              />
              <ConfirmStep
                style={{ display: currentStep === 3 ? 'block' : 'none' }}
                ref={confirmStep}
                {...stepProps}
              />
            </div>
          </div>
        </div>
      </Form>
      {contextHolder}
    </Modal>
  );
};
export default NewOrderModal;
